import { cn } from "@repo/utils/cn";
import { Link } from "../Link";
import { Button, ButtonProps } from "../Button";
import { ChevronRightIcon } from "../icons/ChevronRightIcon";

export interface LinedListItemProps {
  title?: string;
  titleColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
  subTitle?: string;
  price?: string;
  priceColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
  link?: {
    href: string;
    target?: string;
    noFollow?: boolean;
  } | null;
  button?: ButtonProps | null;
  _uid: string;
  className?: string;
}

export function LinedListItem({
  title,
  titleColor = "default",
  subTitle,
  price,
  priceColor = "default",
  link,
  button,
  className,
}: LinedListItemProps) {
  // TODO: temporarily hardcoded to faded-dark, remove when Storyblok data is fixed
  const titleColorClass =
    "uipm:text-[var(--typography-colors-text-faded-dark)]";

  const getPriceColor = () => {
    switch (priceColor) {
      case "default":
        return "var(--surface-surface-accent-tertiary)";
      case "accent":
        return "var(--typography-colors-text-accent)";
      case "faded":
        return "var(--typography-colors-text-faded)";
      case "faded-dark":
        return "var(--typography-colors-text-faded-dark)";
      case "inverted":
        return "var(--typography-colors-text-inverted)";
      default:
        return "var(--surface-surface-accent-tertiary)";
    }
  };

  const content = (
    <div
      className={cn(
        "uipm:flex uipm:items-start uipm:overflow-hidden uipm:pb-[var(--container-margin)] uipm:pt-[var(--container-margin)] uipm:px-0 uipm:w-full",
        link && "uipm:group-hover:bg-[var(--surface-surface-secondary)] uipm:transition-colors uipm:duration-200",
      )}
    >
      <div className="uipm:flex-1 uipm:flex uipm:gap-[var(--padding-padding-default)] uipm:grow uipm:items-center uipm:leading-none uipm:min-h-0 uipm:min-w-0 uipm:not-italic uipm:self-stretch">
        <div
          className={cn(
            "uipm:flex-1 uipm:grow uipm:min-h-0 uipm:min-w-0 uipm:text-(length:--typography-sizes-body-medium)",
            titleColorClass,
          )}
        >
          <p className="uipm:leading-[var(--typography-line-heights-body-medium)] uipm:font-medium">
            {title}
          </p>
          {subTitle && (
            <p className="uipm:leading-[var(--typography-line-heights-body-medium)] uipm:text-[var(--typography-colors-text-faded)]">
              {subTitle}
            </p>
          )}
        </div>
        <div className="uipm:flex uipm:items-center uipm:gap-[var(--padding-padding-default)]">
          {price && (
            <div className="uipm:flex uipm:flex-col uipm:justify-center uipm:text-nowrap uipm:text-right">
              <p
                className="uipm:font-bold uipm:leading-[var(--typography-line-heights-body-medium)] uipm:not-italic uipm:text-(length:--typography-sizes-body-medium) uipm:whitespace-pre"
                style={{ color: getPriceColor() }}
              >
                {price}
              </p>
            </div>
          )}
          {button && (
            <div className="uipm:shrink-0">
              <Button {...button} size="small" />
            </div>
          )}
          {link && !button && (
            <ChevronRightIcon className="uipm:w-5 uipm:h-5 uipm:shrink-0 uipm:text-[var(--surface-surface-accent)]" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn("uipm:relative uipm:w-full", className)}>
      {link && !button ? (
        <Link
          href={link.href}
          target={link.target}
          noFollow={link.noFollow}
          className="uipm:group uipm:block uipm:no-underline"
        >
          {content}
        </Link>
      ) : (
        content
      )}
      <div
        aria-hidden="true"
        className="uipm:absolute uipm:border-b uipm:border-[var(--color-color-light)] uipm:inset-0 uipm:pointer-events-none"
      />
    </div>
  );
}
